import { useEffect, useMemo, useRef } from 'react';
import mapboxgl, { Map as MapboxMap, Popup } from 'mapbox-gl';
import type { Photo, RunSummary } from './types';
import { formatDistance, formatPace, formatShortDate } from './format';
import { useHasHover, useIsDesktop } from './useMedia';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN ?? '';

interface Props {
  runs: RunSummary[];
  photos?: Photo[];
  selectedId: string | null;
  externalHoverId?: string | null;
  showAllPhotos?: boolean;
  flybyActive?: boolean;
  onSelect: (id: string | null) => void;
  onMapReady?: (map: MapboxMap) => void;
}

export const RUNS_SOURCE = 'runs';
export const LAYER_BASE = 'runs-base';
export const LAYER_HOVER = 'runs-hover';
export const LAYER_SELECTED = 'runs-selected';
export const PHOTOS_SOURCE = 'photos';
export const LAYER_PHOTOS = 'photos-layer';
const PHOTO_PIN_ID = 'photo-pin';

// Classic teardrop pin: white fill, dark hairline stroke, accent-red center dot.
// Non-SDF so Mapbox preserves the multi-color fill.
const PHOTO_PIN_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" width="48" height="64" viewBox="0 0 24 32">
  <path d="M12 .75 C5.78.75.75 5.78.75 12 c0 8.5 11.25 19.25 11.25 19.25 S23.25 20.5 23.25 12 C23.25 5.78 18.22.75 12 .75 z"
        fill="#ffffff" stroke="#0B0D10" stroke-width="1.4" stroke-linejoin="round"/>
  <circle cx="12" cy="12" r="3.6" fill="#F25C54"/>
</svg>`.trim();

async function loadPhotoPin(map: MapboxMap): Promise<void> {
  if (map.hasImage(PHOTO_PIN_ID)) return;
  const blob = new Blob([PHOTO_PIN_SVG], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image(48, 64);
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    if (!map.hasImage(PHOTO_PIN_ID)) {
      map.addImage(PHOTO_PIN_ID, img, { pixelRatio: 2 });
    }
  } finally {
    URL.revokeObjectURL(url);
  }
}

function runsToFeatureCollection(runs: RunSummary[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: runs.map((r) => ({
      type: 'Feature',
      id: r.id,
      properties: {
        id: r.id,
        name: r.name,
        date: r.date,
        distance_m: r.distance_m,
        pace: r.avg_pace_s_per_km,
        type: r.type
      },
      geometry: { type: 'LineString', coordinates: r.line }
    }))
  };
}

function photosToFeatureCollection(photos: Photo[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: photos.map((p) => ({
      type: 'Feature',
      id: p.id,
      properties: {
        id: p.id,
        thumbUrl: p.thumbUrl,
        takenAt: p.takenAt,
        runId: p.runId ?? ''
      },
      geometry: { type: 'Point', coordinates: [p.lon, p.lat] }
    }))
  };
}


type BoundsPair = [[number, number], [number, number]];

function runBounds(run: RunSummary): BoundsPair {
  return [
    [run.bbox[0], run.bbox[1]],
    [run.bbox[2], run.bbox[3]]
  ];
}

// Reserve room for summary panel (top-left) + drawer.
// Desktop: drawer on the right. Mobile: drawer along the bottom (varies by mode).
const DESKTOP_PADDING = { top: 160, right: 460, bottom: 80, left: 80 };

// Keep these in sync with the mobile drawer heights in Drawer.tsx.
const MOBILE_DRAWER_SIZES = {
  compact: { ratio: 0.34, maxPx: 360 },
  tall: { ratio: 0.44, maxPx: 480 }
} as const;

function getMobilePadding(mode: 'compact' | 'tall'): mapboxgl.PaddingOptions {
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const size = MOBILE_DRAWER_SIZES[mode];
  const drawerPx = Math.min(Math.round(vh * size.ratio), size.maxPx);
  return { top: 72, right: 24, bottom: drawerPx + 24, left: 24 };
}

export function RunMap({ runs, photos = [], selectedId, externalHoverId = null, showAllPhotos = false, flybyActive = false, onSelect, onMapReady }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const popupRef = useRef<Popup | null>(null);
  const photoPopupRef = useRef<Popup | null>(null);
  const hoveredIdRef = useRef<string | number | null>(null);
  const externalHoverIdRef = useRef<string | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onMapReadyRef = useRef(onMapReady);
  onMapReadyRef.current = onMapReady;
  const initialSelectedIdRef = useRef(selectedId);

  const runsById = useMemo(() => {
    const m = new Map<string, RunSummary>();
    for (const r of runs) m.set(r.id, r);
    return m;
  }, [runs]);
  const runsByIdRef = useRef(runsById);
  runsByIdRef.current = runsById;

  const isDesktop = useIsDesktop();
  const hasHover = useHasHover();
  const fitPadding = isDesktop
    ? DESKTOP_PADDING
    : getMobilePadding(selectedId ? 'tall' : 'compact');

  // initialize map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    // Default center = most recent run's bbox center (runs are sorted newest → oldest)
    const defaultRun = runs[0];
    const defaultBounds = defaultRun ? runBounds(defaultRun) : null;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: defaultBounds
        ? [(defaultBounds[0][0] + defaultBounds[1][0]) / 2, (defaultBounds[0][1] + defaultBounds[1][1]) / 2]
        : [-73.95, 40.75],
      zoom: 11.8,
      pitch: 0,
      bearing: 0,
      attributionControl: false,
      cooperativeGestures: false
    });
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'top-right');
    mapRef.current = map;
    onMapReadyRef.current?.(map);

    map.on('load', () => {
      // Terrain (3D) — subtle, enabled when pitched in
      map.addSource('mapbox-dem', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        maxzoom: 14
      });
      map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.2 });

      map.addLayer({
        id: 'sky',
        type: 'sky',
        paint: {
          'sky-type': 'atmosphere',
          'sky-atmosphere-sun-intensity': 4
        }
      });

      // Building extrusions at close zoom
      const layers = map.getStyle().layers ?? [];
      const firstSymbolId = layers.find((l) => l.type === 'symbol')?.id;
      map.addLayer(
        {
          id: '3d-buildings',
          source: 'composite',
          'source-layer': 'building',
          filter: ['==', 'extrude', 'true'],
          type: 'fill-extrusion',
          minzoom: 14,
          paint: {
            'fill-extrusion-color': '#E8EBEF',
            'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 14, 0, 15, ['get', 'height']],
            'fill-extrusion-base': ['interpolate', ['linear'], ['zoom'], 14, 0, 15, ['get', 'min_height']],
            'fill-extrusion-opacity': 0.8
          }
        },
        firstSymbolId
      );

      // Runs source + layers
      map.addSource(RUNS_SOURCE, {
        type: 'geojson',
        data: runsToFeatureCollection(runs),
        promoteId: 'id',
        lineMetrics: true
      });

      map.addLayer({
        id: LAYER_BASE,
        type: 'line',
        source: RUNS_SOURCE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': [
            'case',
            ['boolean', ['feature-state', 'dimmed'], false],
            '#A6ADB8',
            '#F25C54'
          ],
          'line-width': ['interpolate', ['linear'], ['zoom'], 9, 1.4, 13, 2.2, 16, 3],
          'line-opacity': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            0,
            ['boolean', ['feature-state', 'hover'], false],
            0,
            ['boolean', ['feature-state', 'dimmed'], false],
            0.35,
            0.6
          ]
        }
      });

      map.addLayer({
        id: LAYER_HOVER,
        type: 'line',
        source: RUNS_SOURCE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#0B0D10',
          'line-width': ['interpolate', ['linear'], ['zoom'], 9, 2.6, 13, 3.6, 16, 4.4],
          'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.95, 0]
        }
      });

      map.addLayer({
        id: LAYER_SELECTED,
        type: 'line',
        source: RUNS_SOURCE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#F25C54',
          'line-width': ['interpolate', ['linear'], ['zoom'], 9, 2.8, 13, 4.2, 16, 5.4],
          'line-opacity': ['case', ['boolean', ['feature-state', 'selected'], false], 1, 0]
        }
      });

      // Photos: small circle markers sitting above run lines.
      // Circle layers don't need an icon image and stay crisp at every zoom.
      map.addSource(PHOTOS_SOURCE, {
        type: 'geojson',
        data: photosToFeatureCollection([]),
        promoteId: 'id'
      });

      loadPhotoPin(map)
        .then(() => {
          if (map.getLayer(LAYER_PHOTOS) || !map.getSource(PHOTOS_SOURCE)) return;
          map.addLayer({
            id: LAYER_PHOTOS,
            type: 'symbol',
            source: PHOTOS_SOURCE,
            // Hidden by default; the active-run effect widens this filter to
            // show photos only for the hovered / selected run.
            filter: ['==', ['get', 'runId'], '__none__'],
            layout: {
              'icon-image': PHOTO_PIN_ID,
              'icon-size': ['interpolate', ['linear'], ['zoom'], 9, 0.35, 13, 0.5, 16, 0.65],
              'icon-anchor': 'bottom',
              'icon-allow-overlap': true,
              'icon-ignore-placement': true
            }
          });
        })
        .catch((err) => console.error('failed to load photo pin', err));

      map.on('mouseenter', LAYER_PHOTOS, (e) => {
        if (!e.features || e.features.length === 0) return;
        const props = e.features[0].properties ?? {};
        const thumb = String(props.thumbUrl ?? '');
        const runId = String(props.runId ?? '');
        const run = runId ? runsByIdRef.current.get(runId) : undefined;
        const caption = run
          ? `${escapeHtml(run.name)} · ${formatShortDate(run.date)}`
          : 'Photo';
        const html = `
          <div style="width:180px">
            <img src="${thumb}" alt="" style="width:180px;height:120px;object-fit:cover;border-radius:8px;display:block" loading="lazy" />
            <div class="text-ink-700 text-xs mt-1.5 leading-tight">${caption}</div>
          </div>
        `;
        if (!photoPopupRef.current) {
          photoPopupRef.current = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            // Offset clears the teardrop head; pin is bottom-anchored at the coord.
            offset: 36,
            maxWidth: '220px'
          });
        }
        const coords = (e.features[0].geometry as GeoJSON.Point).coordinates as [number, number];
        photoPopupRef.current.setLngLat(coords).setHTML(html).addTo(map);
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', LAYER_PHOTOS, () => {
        photoPopupRef.current?.remove();
        map.getCanvas().style.cursor = '';
      });

      map.on('click', LAYER_PHOTOS, (e) => {
        if (!e.features || e.features.length === 0) return;
        const runId = String(e.features[0].properties?.runId ?? '');
        if (runId) {
          onSelectRef.current(runId);
          // Prevent the map-level "click on empty" handler from clearing selection.
          e.originalEvent.stopPropagation?.();
        }
      });

      // Hover behavior
      map.on('mousemove', LAYER_BASE, (e) => {
        if (!e.features || e.features.length === 0) return;
        const id = e.features[0].id;
        if (hoveredIdRef.current != null && hoveredIdRef.current !== id) {
          map.setFeatureState({ source: RUNS_SOURCE, id: hoveredIdRef.current }, { hover: false });
        }
        hoveredIdRef.current = id ?? null;
        if (id != null) {
          map.setFeatureState({ source: RUNS_SOURCE, id }, { hover: true });
        }
        map.getCanvas().style.cursor = 'pointer';

        const props = e.features[0].properties ?? {};
        const name = String(props.name ?? 'Run');
        const date = String(props.date ?? '');
        const dist = Number(props.distance_m ?? 0);
        const pace = Number(props.pace ?? 0);

        const html = `
          <div class="text-ink-900 font-medium">${escapeHtml(name)}</div>
          <div class="text-ink-500 flex gap-2 mt-0.5">
            <span>${date ? formatShortDate(date) : ''}</span>
            <span>·</span>
            <span>${formatDistance(dist, 'mi')}</span>
            <span>·</span>
            <span>${formatPace(pace, 'mi')}</span>
          </div>
        `;
        if (!popupRef.current) {
          popupRef.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12 });
        }
        popupRef.current.setLngLat(e.lngLat).setHTML(html).addTo(map);
      });

      map.on('mouseleave', LAYER_BASE, () => {
        if (hoveredIdRef.current != null) {
          map.setFeatureState({ source: RUNS_SOURCE, id: hoveredIdRef.current }, { hover: false });
        }
        hoveredIdRef.current = null;
        map.getCanvas().style.cursor = '';
        popupRef.current?.remove();
      });

      map.on('click', LAYER_BASE, (e) => {
        if (!e.features || e.features.length === 0) return;
        const id = e.features[0].id;
        if (typeof id === 'string' || typeof id === 'number') {
          onSelectRef.current(String(id));
        }
      });

      // Click on empty space deselects
      map.on('click', (e) => {
        const layers = [LAYER_BASE];
        if (map.getLayer(LAYER_PHOTOS)) layers.push(LAYER_PHOTOS);
        const hits = map.queryRenderedFeatures(e.point, { layers });
        if (hits.length === 0) onSelectRef.current(null);
      });

      // Initial fit — only when nothing is preselected. If selectedId was
      // present at mount (deep link), let the focus effect frame the
      // selected run instead.
      if (defaultBounds && !initialSelectedIdRef.current) {
        map.fitBounds(defaultBounds, { padding: fitPadding, duration: 0, maxZoom: 12 });
      }
    });

    return () => {
      popupRef.current?.remove();
      popupRef.current = null;
      photoPopupRef.current?.remove();
      photoPopupRef.current = null;
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // update data when runs change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const src = map.getSource(RUNS_SOURCE) as mapboxgl.GeoJSONSource | undefined;
      if (src) src.setData(runsToFeatureCollection(runs));
    };
    if (map.isStyleLoaded() && map.getSource(RUNS_SOURCE)) apply();
    else map.once('idle', apply);
  }, [runs]);

  // update photos when they change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const src = map.getSource(PHOTOS_SOURCE) as mapboxgl.GeoJSONSource | undefined;
      if (src) src.setData(photosToFeatureCollection(photos));
    };
    if (map.isStyleLoaded() && map.getSource(PHOTOS_SOURCE)) apply();
    else map.once('idle', apply);
  }, [photos]);

  // Photo pins: shown for the selected run, or for all runs when the user
  // toggles "show all photos" on the main view. Rendering pins on hover caused
  // a visible stutter on the card's line-color highlight.
  const showAllPins = showAllPhotos && !selectedId;
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      if (!map.getLayer(LAYER_PHOTOS)) return;
      if (showAllPins) {
        map.setFilter(LAYER_PHOTOS, ['has', 'id']);
      } else {
        map.setFilter(LAYER_PHOTOS, ['==', ['get', 'runId'], selectedId ?? '__none__']);
        if (!selectedId) photoPopupRef.current?.remove();
      }
    };
    if (map.isStyleLoaded() && map.getLayer(LAYER_PHOTOS)) apply();
    else map.once('idle', apply);
  }, [selectedId, showAllPins]);

  // External hover (from run cards) → highlight corresponding line on map (instant)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      if (!map.getSource(RUNS_SOURCE)) return;
      const prev = externalHoverIdRef.current;
      if (prev && prev !== externalHoverId) {
        map.setFeatureState({ source: RUNS_SOURCE, id: prev }, { hover: false });
      }
      if (externalHoverId) {
        map.setFeatureState({ source: RUNS_SOURCE, id: externalHoverId }, { hover: true });
      }
      externalHoverIdRef.current = externalHoverId;
    };
    if (map.isStyleLoaded() && map.getSource(RUNS_SOURCE)) apply();
    else map.once('idle', apply);
  }, [externalHoverId]);

  // Focus behavior: selected > hover > most-recent.
  // Hover-driven fly-to is debounced so scanning the list doesn't thrash.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || runs.length === 0) return;
    // Flyby owns the camera while active; skip the focus animation.
    if (flybyActive) return;

    // Always update selected + dimmed feature-state immediately.
    // When a run is selected, every other run is dimmed (renders as gray on the map).
    const paintSelected = () => {
      if (!map.getSource(RUNS_SOURCE)) return;
      const anySelected = !!selectedId;
      for (const r of runs) {
        const isSelected = r.id === selectedId;
        map.setFeatureState(
          { source: RUNS_SOURCE, id: r.id },
          { selected: isSelected, dimmed: anySelected && !isSelected }
        );
      }
    };
    if (map.isStyleLoaded() && map.getSource(RUNS_SOURCE)) paintSelected();
    else map.once('idle', paintSelected);

    // Compute focus target
    let focusRun: RunSummary | undefined;
    let pitch = 0;
    let delay = 0;

    if (selectedId) {
      focusRun = runs.find((r) => r.id === selectedId);
      pitch = 50;
    } else if (externalHoverId) {
      // Triggered by hovering a card (desktop) or swiping the card carousel (mobile).
      focusRun = runs.find((r) => r.id === externalHoverId);
      // No debounce — hover highlight should feel immediate. The setTimeout
      // cleanup below still cancels a pending animation if the user scans on.
      delay = 0;
    } else {
      focusRun = runs[0];
    }

    if (!focusRun) return;

    const target = focusRun;
    const isSelected = !!selectedId;
    // Frame cap per mode:
    //   selected → 15 (close-up on the run)
    //   hover/default → 11 (city-wide; NYC fits comfortably)
    // `maxZoom` caps fitBounds so a small run's bbox still yields a wide view.
    const zoomCap = isSelected ? 15 : 12;

    const timer = window.setTimeout(() => {
      map.easeTo({ pitch, duration: isSelected ? 400 : 200 });
      map.fitBounds(runBounds(target), {
        padding: fitPadding,
        pitch,
        duration: isSelected ? 1100 : 400,
        essential: true,
        maxZoom: zoomCap
      });
    }, delay);

    return () => window.clearTimeout(timer);
  }, [selectedId, externalHoverId, runs, hasHover, fitPadding, flybyActive]);

  return <div ref={containerRef} className="absolute inset-0" />;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}
